import { JsonResponseData } from "csi/comm/JsonResponse";
import { REQUEST_MODULE, REQUEST_OPERATION, createFormData, postFormData } from "../core/request";
import { C8Node } from "../type/node";
import { cnl } from "../type/value";

export interface ICustomViewInfoPartyCustomViewBase {
	/** name of the owner, either role or user */
	readonly name: string;

	/** url of the owner, either role or user */
	readonly url: cnl;
}

export interface ICustomViewInfoPartyCustomView extends ICustomViewInfoPartyCustomViewBase {
	/** urls of the custom views */
	readonly views: cnl[];
}

export interface ICustomViewInfoResponse {
	/** descriptions of the custom views */
	descriptions: Record<cnl, string>;

	/** favorite views */
	favorites: cnl[];

	/** names of the custom views */
	names: Record<cnl, string>;

	/** person owned custom views */
	personal: ICustomViewInfoPartyCustomView[];

	/** role based custom views */
	roles: ICustomViewInfoPartyCustomView[];

	/** global custom views */
	systemWide: cnl[];
}

/** get the validated custom view node - for use cases that do not support a source view (i.e. attribute cards, filter attributes, etc.) */
export async function getValidatedCustomView(customView: cnl): Promise<C8Node | undefined> {
	const result = await getValidatedCustomViewWithSourceView(customView);
	return result?.[0];
}

/** get the validated custom view node along with its source view, if present */
export async function getValidatedCustomViewWithSourceView(customView: cnl): Promise<[C8Node, C8Node | undefined] | undefined> {
	const formData = createFormData({
		[REQUEST_MODULE]: "SiteAdmin",
		[REQUEST_OPERATION]: "GetValidatedCustomView",
		URL: customView,
	});

	const result = await postFormData<JsonResponseData>(formData);
	const resultNode = result.NODES.ResultNode?.[0];
	if (resultNode) {
		return [resultNode, result.NODES.Node?.[0]];
	}
}

/**
 * get the available custom views based on the current container and user context
 * @param container 
 * @returns 
 */
export async function queryCustomViews(container: cnl, rootNodeType?: string): Promise<ICustomViewInfoResponse> {
	const formData = createFormData({
		[REQUEST_MODULE]: "SiteAdmin",
		[REQUEST_OPERATION]: "QueryCustomViews",
		URL: container,
		RootNodeType: rootNodeType || "",
	});

	const result = await postFormData<ICustomViewInfoResponse>(formData);
	return result;
}

/** reorder the favorite views */
export async function reorderFavorites(user: cnl, container: cnl, views: cnl[]): Promise<C8Node | undefined> {
	const formData = createFormData({
		[REQUEST_MODULE]: "SiteAdmin",
		[REQUEST_OPERATION]: "ReorderFavoriteCustomView",
		ContainerURL: container,
		User: user,
		PreferenceViews: views,
	});

	const result = await postFormData<JsonResponseData>(formData);
	return result.NODES.ResultNode?.[0];
}